import { io } from 'socket.io-client';
import { store } from '../store/store';

class WebSocketService {
  constructor() {
    this.socket = null;
    this.listeners = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 3000;
    this.isConnecting = false;
    this.subscribedInstruments = new Set();
    this.pingInterval = null;
    this.connectionStatus = 'disconnected';
    this.statusListeners = new Set();
    this.pendingMessages = [];
  }

  connect() {
    if (this.isConnecting || (this.socket && this.socket.connected)) return;

    this.isConnecting = true;
    this.updateStatus('connecting');

    const serverUrl = import.meta.env.VITE_WS_URL || 'http://localhost:3000';

    try {
      const state = store.getState();
      const token = state.auth?.token || localStorage.getItem('token') || null;

      this.socket = io(serverUrl, {
        auth: { token },
        transports: ['websocket', 'polling'],
        reconnection: false,
        timeout: 10000,
      });

      this.socket.on('connect', this.handleOpen.bind(this));
      this.socket.on('disconnect', this.handleClose.bind(this));
      this.socket.on('connect_error', this.handleError.bind(this));

      // Price events
      this.socket.on('price', (data) => this.notifyListeners('price', data));
      this.socket.on('bulk_prices', (data) => {
        if (Array.isArray(data)) data.forEach(price => this.notifyListeners('price', price));
      });
      
      // Trade execution events
      this.socket.on('trade_executed', (data) => this.notifyListeners('trade', data));
      this.socket.on('order_filled', (data) => this.notifyListeners('order', data));
      
      // Position events
      this.socket.on('position_update', (data) => this.notifyListeners('position_update', data));
      this.socket.on('position_closed', (data) => this.notifyListeners('position_closed', data));
      this.socket.on('positions_update', (data) => this.notifyListeners('positions_update', data));
      
      // Balance events
      this.socket.on('balance_update', (data) => this.notifyListeners('balance_update', data));
      this.socket.on('balance', (data) => this.notifyListeners('balance', data));
      
      // Trade history events
      this.socket.on('trade_notification', (data) => this.notifyListeners('trade_notification', data));
      this.socket.on('trade_history', (data) => this.notifyListeners('trade_history', data));
      
      // Pending order events
      this.socket.on('pending_orders', (data) => this.notifyListeners('pending_orders', data));
      this.socket.on('order_cancelled', (data) => this.notifyListeners('order_cancelled', data));
      
      // Notification events
      this.socket.on('notification', (data) => this.notifyListeners('notification', data));
      this.socket.on('new_notification', (data) => this.notifyListeners('new_notification', data));
      
      // User profile/update events (e.g., badge changes from admin)
      this.socket.on('user_updated', (data) => this.notifyListeners('user_updated', data));
      
      // System events
      this.socket.on('pong', () => {});
      this.socket.on('status', (data) => this.updateStatus(data.status));
      this.socket.on('connection_established', (data) => {
        this.notifyListeners('connection_established', data);
      });
      this.socket.on('user_subscribed', (data) => this.notifyListeners('user_subscribed', data));
      this.socket.on('user_unsubscribed', (data) => this.notifyListeners('user_unsubscribed', data));
      this.socket.on('system_message', (data) => this.notifyListeners('system_message', data));
      this.socket.on('error', (data) => this.notifyListeners('error', data));
      this.socket.on('subscriptions', (data) => this.notifyListeners('subscriptions', data));
      this.socket.on('subscribed', (data) => this.notifyListeners('subscribed', data));
      this.socket.on('unsubscribed', (data) => this.notifyListeners('unsubscribed', data));

    } catch (error) {
      this.isConnecting = false;
      this.handleReconnect();
    }
  }

  handleOpen() {
    this.isConnecting = false;
    this.reconnectAttempts = 0;
    this.updateStatus('connected');
    this.startPingInterval();
    this.processPendingMessages();

    if (this.subscribedInstruments.size > 0) {
      this.send('subscribe', { symbols: Array.from(this.subscribedInstruments) });
    }

    this.emitPageChange();
  }

  handleClose(reason) {
    this.stopPingInterval();
    this.updateStatus('disconnected');
    this.isConnecting = false;
    if (reason !== 'io client disconnect') this.handleReconnect();
  }

  handleError(error) {
    this.isConnecting = false;
    this.updateStatus('error');
    this.handleReconnect();
  }

  handleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.updateStatus('failed');
      return;
    }
    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(1.5, this.reconnectAttempts - 1);
    setTimeout(() => this.connect(), delay);
  }

  startPingInterval() {
    this.pingInterval = setInterval(() => {
      if (this.isConnected()) this.send('ping', {});
    }, 30000);
  }

  stopPingInterval() {
    if (this.pingInterval) { clearInterval(this.pingInterval); this.pingInterval = null; }
  }

  emitPageChange() {
    const path = window.location.pathname;
    this.send('page_change', { page: path });
  }

  send(event, payload = {}) {
    if (this.socket && this.socket.connected) {
      this.socket.emit(event, payload);
    } else {
      this.pendingMessages.push({ event, payload });
    }
  }

  processPendingMessages() {
    if (this.pendingMessages.length > 0 && this.isConnected()) {
      const messages = [...this.pendingMessages];
      this.pendingMessages = [];
      messages.forEach(({ event, payload }) => this.send(event, payload));
    }
  }

  subscribe(symbols) {
    const symbolArray = Array.isArray(symbols) ? symbols : [symbols];
    const newSymbols = symbolArray.filter(s => !this.subscribedInstruments.has(s));
    if (newSymbols.length === 0) return;
    newSymbols.forEach(s => this.subscribedInstruments.add(s));
    this.send('subscribe', { symbols: newSymbols });
  }

  unsubscribe(symbols) {
    const symbolArray = Array.isArray(symbols) ? symbols : [symbols];
    const existingSymbols = symbolArray.filter(s => this.subscribedInstruments.has(s));
    if (existingSymbols.length === 0) return;
    existingSymbols.forEach(s => this.subscribedInstruments.delete(s));
    this.send('unsubscribe', { symbols: existingSymbols });
  }

  subscribeUser(userId) { this.send('subscribe_user', { userId }); }
  unsubscribeUser(userId) { this.send('unsubscribe_user', { userId }); }

  on(event, callback) {
    if (!this.listeners.has(event)) this.listeners.set(event, new Set());
    this.listeners.get(event).add(callback);
  }

  off(event, callback) {
    if (this.listeners.has(event)) this.listeners.get(event).delete(callback);
  }

  notifyListeners(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(cb => { try { cb(data); } catch (e) {} });
    }
  }

  onStatusChange(callback) { this.statusListeners.add(callback); callback(this.connectionStatus); }
  offStatusChange(callback) { this.statusListeners.delete(callback); }

  updateStatus(status) {
    this.connectionStatus = status;
    this.statusListeners.forEach(cb => cb(status));
  }

  isConnected() { return this.socket && this.socket.connected; }
  getStatus() { return this.connectionStatus; }

  disconnect() {
    this.stopPingInterval();
    if (this.socket) { this.socket.disconnect(); this.socket = null; }
    this.isConnecting = false;
    this.updateStatus('disconnected');
  }

  reconnect() { this.disconnect(); this.reconnectAttempts = 0; this.connect(); }
}

const websocketService = new WebSocketService();
export const getSocket = () => websocketService.socket;
export default websocketService;